import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Lightbulb, Award, Target, Zap } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const icons = [TrendingUp, Lightbulb, Award, Target];
const iconColors = [
  'from-[var(--accent-teal)] to-[var(--accent-blue)]',
  'from-[var(--accent-gold)] to-orange-500',
  'from-purple-500 to-pink-500',
  'from-[var(--accent-blue)] to-[var(--accent-teal)]',
];

const WhyCommunication = () => {
  const { whyCommunicationContent } = useContent();
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section id="about" className="py-16 md:py-24 bg-pattern relative overflow-hidden" data-testid="about-section">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{ 
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 left-0 w-96 h-96 rounded-full bg-[var(--accent-teal)] blur-[150px] opacity-10"
        />
        <motion.div
          animate={{ 
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full bg-[var(--accent-gold)] blur-[150px] opacity-10"
        />
      </div>

      <div className="container-main relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <Zap className="w-5 h-5 text-[var(--accent-gold)]" />
            <span className="text-sm font-medium text-[var(--accent-gold)] uppercase tracking-wider">Why It Matters</span>
            <Zap className="w-5 h-5 text-[var(--accent-gold)]" />
          </motion.div>
          
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-4 px-2">
            Your Skills Open Doors.
            <br />
            <span className="gradient-text">Your Communication</span> Decides How Far You Go.
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[var(--accent-gold)] font-medium max-w-2xl mx-auto px-4">
            {whyCommunicationContent.subheadline}
          </p>
        </motion.div>

        {/* Cards Grid - Bento Style */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {whyCommunicationContent.points.map((point, index) => {
            const Icon = icons[index];
            const isHovered = hoveredCard === index;
            
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                className="relative group"
              >
                <motion.div
                  animate={{
                    y: isHovered ? -10 : 0,
                    scale: isHovered ? 1.02 : 1,
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="interactive-card p-6 h-full shimmer-border"
                >
                  {/* Number badge */}
                  <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                    <span className="text-xs font-bold text-[var(--text-muted)]">0{index + 1}</span>
                  </div>

                  {/* Icon with gradient background */}
                  <motion.div 
                    animate={{
                      rotate: isHovered ? 10 : 0,
                      scale: isHovered ? 1.1 : 1,
                    }}
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${iconColors[index]} flex items-center justify-center mb-5 shadow-lg`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </motion.div>

                  {/* Title */}
                  <h3 className="font-display text-xl font-semibold text-[var(--text-primary)] mb-3 group-hover:text-[var(--accent-teal)] transition-colors">
                    {point.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[var(--text-muted)] leading-relaxed text-sm">
                    {point.description}
                  </p>

                  {/* Decorative line */}
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: isHovered ? '100%' : '0%' }}
                    transition={{ duration: 0.3 }}
                    className={`absolute bottom-0 left-0 h-1 rounded-b-xl bg-gradient-to-r ${iconColors[index]}`}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyCommunication;
